/**
 * Queue behind the imperative `message` API. Kept free of React so it can be
 * unit-tested directly and consumed by a single rendered host.
 */

const DEFAULT_DURATION = 3

let entries = []
let sequence = 0
const listeners = new Set()
const timers = new Map()

const emit = () => {
  listeners.forEach((listener) => listener(entries))
}

export const subscribe = (listener) => {
  listeners.add(listener)
  listener(entries)
  return () => listeners.delete(listener)
}

export const getEntries = () => entries

export const remove = (id) => {
  clearTimeout(timers.get(id))
  timers.delete(id)
  const next = entries.filter((entry) => entry.id !== id)
  if (next.length === entries.length) return
  entries = next
  emit()
}

export const destroyAll = (key) => {
  if (key != null) {
    entries.filter((entry) => entry.key === key).forEach((entry) => remove(entry.id))
    return
  }
  timers.forEach((timer) => clearTimeout(timer))
  timers.clear()
  entries = []
  emit()
}

/**
 * Adds or replaces a toast.
 *
 * antd semantics honored here:
 *  - a repeated `key` replaces the existing toast in place instead of stacking
 *  - `duration: 0` keeps it open until the returned hide function is called
 *  - the return value is the hide function
 *
 * @param {{type: string, content: any, duration?: number, key?: string|number, onClose?: Function, icon?: any, className?: string, style?: object}} config
 * @returns {() => void} hide
 */
export const push = (config) => {
  sequence += 1
  const id = `message-${sequence}`
  const duration = config.duration ?? DEFAULT_DURATION
  const entry = { ...config, id, duration }

  const existingIndex = config.key != null ? entries.findIndex((item) => item.key === config.key) : -1

  if (existingIndex >= 0) {
    const previous = entries[existingIndex]
    clearTimeout(timers.get(previous.id))
    timers.delete(previous.id)
    entries = entries.map((item, index) => (index === existingIndex ? { ...entry, id: previous.id } : item))
    entry.id = previous.id
  } else {
    entries = [...entries, entry]
  }

  emit()

  if (duration > 0) {
    timers.set(
      entry.id,
      setTimeout(() => {
        remove(entry.id)
        config.onClose?.()
      }, duration * 1000)
    )
  }

  return () => {
    remove(entry.id)
    config.onClose?.()
  }
}

/** Test seam: drops all state and listeners. */
export const reset = () => {
  destroyAll()
  listeners.clear()
  sequence = 0
}

export { DEFAULT_DURATION }
